import type { FC } from 'react';

import MenuIcon from '@/assets/svg/header/menu.svg';
import LogoMain from '@/assets/svg/logos/logo-main.svg';
import LogoMark from '@/assets/svg/logos/logo-mark.svg';
import CircleHelpIcon from '@/assets/svg/ui-elements/circle-help.svg';

import Button, { IconButton } from '../Button';
import Separator from '../Separator';
import {
  HeaderActionsGroup,
  HeaderDivider,
  HeaderLeft,
  HeaderRight,
  LOGO_FULL_HEIGHT,
  LOGO_FULL_WIDTH,
  LOGO_MARK_HEIGHT,
  LOGO_MARK_WIDTH,
  LogoLink,
  StyledHeader,
} from './GlobalHeader.styled';
import type { GlobalHeaderProps } from './types';

const Divider = () => (
  <HeaderDivider>
    <Separator orientation="vertical" tone="border" />
  </HeaderDivider>
);

const GlobalHeader: FC<GlobalHeaderProps> = ({
  auth = 'signed-in',
  viewport = 'desktop',
  showHelp = false,
  onHelpClick,
  logoHref = '/',
  onMenuClick,
  sidebarToggle,
  search,
  notice,
  actions,
  wallet,
  userDropdown,
  ...rest
}) => {
  const isMobile = viewport === 'mobile';
  const signedIn = auth === 'signed-in';

  const logo = (
    <LogoLink to={logoHref} aria-label="Home">
      {isMobile ? (
        <LogoMark width={LOGO_MARK_WIDTH} height={LOGO_MARK_HEIGHT} />
      ) : (
        <LogoMain width={LOGO_FULL_WIDTH} height={LOGO_FULL_HEIGHT} />
      )}
    </LogoLink>
  );

  return (
    <StyledHeader $viewport={viewport} {...rest}>
      {signedIn && !isMobile ? (
        <HeaderLeft>
          {sidebarToggle}
          {search}
        </HeaderLeft>
      ) : (
        logo
      )}

      {signedIn && !isMobile && (
        <HeaderRight $viewport={viewport}>
          {notice}

          <HeaderActionsGroup>
            {actions}
            {wallet}
          </HeaderActionsGroup>

          {userDropdown && (
            <>
              <Divider />
              {userDropdown}
            </>
          )}
        </HeaderRight>
      )}

      {signedIn && isMobile && (
        <HeaderRight $viewport={viewport}>
          {userDropdown}

          {userDropdown && <Divider />}

          <HeaderActionsGroup>
            {wallet}
            {actions}
            {onMenuClick && (
              <IconButton
                type="button"
                color="tertiary"
                variant="solid"
                size="xs"
                aria-label="Open menu"
                onClick={onMenuClick}
              >
                <MenuIcon aria-hidden="true" />
              </IconButton>
            )}
          </HeaderActionsGroup>
        </HeaderRight>
      )}

      {!signedIn && showHelp && onHelpClick && (
        <Button type="button" color="tertiary" variant="ghost" size="sm" onClick={onHelpClick}>
          <CircleHelpIcon aria-hidden="true" />
          Help
        </Button>
      )}
    </StyledHeader>
  );
};

GlobalHeader.displayName = 'GlobalHeader';

export default GlobalHeader;
